<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import Navbar from '@/components/Navbar.vue'
import InstitutionAutocomplete from '@/components/credit-search/InstitutionAutocomplete.vue'
import CourseResultTable from '@/components/credit-search/CourseResultTable.vue'
import SubjectResultTable from '@/components/credit-search/SubjectResultTable.vue'
import PaginationControls from '@/components/credit-search/PaginationControls.vue'
import ResultSection from '@/components/credit-search/ResultSection.vue'
import CreditPreviewModal from '@/components/credit-search/CreditPreviewModal.vue'
import { useCreditSearchApi } from '@/composables/credit-search/useCreditSearchApi'
import type {
  CreditSearchConfiguration,
  CreditSearchCourse,
  CreditSearchInstitution,
  CreditSearchSubject,
  CreditSearchCourseResponse,
  CreditSearchSubjectResponse,
} from '@/models/credit-search/api'
import { courseSelectionKey, subjectSelectionKey } from '@/models/credit-search/selection'
import type { CreditSearchPreviewData } from '@/models/credit-search/preview'
import { buildCreditSearchPreviewPayload } from '@/models/credit-search/preview'

defineOptions({ name: 'CreditSearchApp' })

const api = useCreditSearchApi()
const institutions = ref<CreditSearchInstitution[]>([])
const selectedInstitution = ref<CreditSearchInstitution | null>(null)
const searchInput = ref('')
const activeSearchTerm = ref('')
const courses = ref<CreditSearchCourse[]>([])
const subjects = ref<CreditSearchSubject[]>([])
const courseTotal = ref(0)
const subjectTotal = ref(0)
const courseOffset = ref(0)
const subjectOffset = ref(0)
const courseLimit = ref(api.pageSize)
const subjectLimit = ref(api.pageSize)
const courseLoading = ref(false)
const subjectLoading = ref(false)
const courseResolved = ref(false)
const subjectResolved = ref(false)
const selectedCourses = ref<Record<string, CreditSearchCourse>>({})
const selectedSubjects = ref<Record<string, CreditSearchSubject>>({})
const configuration = ref<CreditSearchConfiguration | null>(null)
const preview = ref<CreditSearchPreviewData | null>(null)
const previewOpen = ref(false)
const previewLoading = ref(false)
const previewRefreshing = ref(false)
const previewError = ref<string | null>(null)
const previewTrigger = ref<HTMLButtonElement | null>(null)
const disclaimerHighlighted = ref(false)

const resultsVisible = computed(() => courseResolved.value || subjectResolved.value)
const selectedCourseKeys = computed(() => new Set(Object.keys(selectedCourses.value)))
const selectedCourseKey = computed(() => Object.keys(selectedCourses.value)[0] ?? null)
const selectedSubjectKeys = computed(() => new Set(Object.keys(selectedSubjects.value)))
const selectedCount = computed(() =>
  Object.keys(selectedCourses.value).length + Object.keys(selectedSubjects.value).length,
)

onMounted(() => {
  void api.getConfig().then((value) => { configuration.value = value }).catch(() => undefined)
  void api.getInstitutions().then((value) => { institutions.value = value }).catch(() => undefined)
})

function search(): void {
  activeSearchTerm.value = searchInput.value
  courseOffset.value = 0
  subjectOffset.value = 0
  loadCourses(false)
  loadSubjects(false)
}

function loadCourses(scrollToSearch: boolean): void {
  courseLoading.value = true
  const requestedOffset = courseOffset.value
  void api.getCourses(
    selectedInstitution.value?.id ?? null,
    activeSearchTerm.value,
    requestedOffset,
  ).then((response: CreditSearchCourseResponse | null) => {
    if (response === null) return
    courses.value = response.courses
    courseLimit.value = response.limit
    courseTotal.value = response.total
    courseOffset.value = response.offset
    courseResolved.value = true
    courseLoading.value = false
    if (scrollToSearch) scrollTo('PreviousStudyBox')
  }).catch(() => {
    courseLoading.value = false
    courseResolved.value = true
  })
}

function loadSubjects(scrollToHeader: boolean): void {
  subjectLoading.value = true
  const requestedOffset = subjectOffset.value
  void api.getSubjects(
    selectedInstitution.value?.id ?? null,
    activeSearchTerm.value,
    requestedOffset,
  ).then((response: CreditSearchSubjectResponse | null) => {
    if (response === null) return
    subjects.value = response.subjects
    subjectLimit.value = response.limit
    subjectTotal.value = response.total
    subjectOffset.value = response.offset
    subjectResolved.value = true
    subjectLoading.value = false
    if (scrollToHeader) scrollTo('subjectListHeaderBtn')
  }).catch(() => {
    subjectLoading.value = false
    subjectResolved.value = true
  })
}

function selectCourse(course: CreditSearchCourse): void {
  if (course.institution_id === null && selectedInstitution.value === null) {
    window.alert('Your institution is required before proceeding. Please enter your institution and search again.')
    document.getElementById('institutionSeachBox')?.focus()
    return
  }

  const key = courseSelectionKey(course)
  if (selectedCourses.value[key]) {
    removeCourse(course)
    return
  }

  selectedCourses.value = { [key]: course }
}

function removeCourse(course: CreditSearchCourse): void {
  const next = { ...selectedCourses.value }
  delete next[courseSelectionKey(course)]
  selectedCourses.value = next
}

function selectSubject(subject: CreditSearchSubject): void {
  const key = subjectSelectionKey(subject)
  if (selectedSubjects.value[key]) {
    removeSubject(subject)
    return
  }
  selectedSubjects.value = { ...selectedSubjects.value, [key]: subject }
}

function removeSubject(subject: CreditSearchSubject): void {
  const next = { ...selectedSubjects.value }
  delete next[subjectSelectionKey(subject)]
  selectedSubjects.value = next
}

function moveCoursePage(direction: -1 | 1): void {
  if (courseLoading.value) return
  courseOffset.value = Math.max(0, courseOffset.value + direction * courseLimit.value)
  loadCourses(true)
}

function moveSubjectPage(direction: -1 | 1): void {
  if (subjectLoading.value) return
  subjectOffset.value = Math.max(0, subjectOffset.value + direction * subjectLimit.value)
  loadSubjects(true)
}

function onSectionOpened(section: 'course' | 'subject'): void {
  scrollTo(section === 'course' ? 'courseListHeaderBtn' : 'subjectListHeaderBtn')
}

function scrollTo(id: string): void {
  window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 200)
}

function showPotentialCredit(event: MouseEvent): void {
  if (selectedCount.value === 0 || previewLoading.value) return
  previewTrigger.value = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null
  previewOpen.value = true
  preview.value = null
  previewError.value = null
  requestPreview(false)
}

function requestPreview(refreshing: boolean): void {
  if (selectedCount.value === 0) {
    closePreview()
    return
  }

  previewLoading.value = true
  previewRefreshing.value = refreshing
  const payload = buildCreditSearchPreviewPayload(
    selectedSubjects.value,
    Object.values(selectedCourses.value),
    selectedInstitution.value,
  )

  void api.getPreview(payload).then((result) => {
    preview.value = result
    previewError.value = null
  }).catch(() => {
    previewError.value = 'Unable to calculate potential credit. Please try again.'
  }).finally(() => {
    previewLoading.value = false
    previewRefreshing.value = false
  })
}

function closePreview(): void {
  previewOpen.value = false
  previewLoading.value = false
  previewRefreshing.value = false
  void nextTick(() => previewTrigger.value?.focus())
}

function removePreviewSubject(selectionKey: string): void {
  if (previewRefreshing.value || !selectedSubjects.value[selectionKey]) return
  const next = { ...selectedSubjects.value }
  delete next[selectionKey]
  selectedSubjects.value = next
  requestPreview(true)
}

function removePreviewCourse(selectionKey: string): void {
  if (previewRefreshing.value || !selectedCourses.value[selectionKey]) return
  const next = { ...selectedCourses.value }
  delete next[selectionKey]
  selectedCourses.value = next
  requestPreview(true)
}

function showDisclaimer(): void {
  closePreview()
  disclaimerHighlighted.value = true
  window.setTimeout(() => document.getElementById('top')?.scrollIntoView({ behavior: 'smooth' }), 200)
}
</script>

<template>
  <header>
    <Navbar active="home" />
  </header>
  <main id="top" class="container credit-search-page">
    <div class="starter-template">
      <div id="maindiv">
        <div class="infoArea">
          <h1>La Trobe Credit (Advanced Standing) Search</h1>
          <div id="calculatorPreText" class="calculatorPreText" :class="{ alert: disclaimerHighlighted, 'alert-warning': disclaimerHighlighted }">
            <p>
              You can search here for indicative credit availability. Please note that this is a guide only.
              Credit is assessed formally on a case by case basis and can only be confirmed during the admissions
              and enrolment process.
            </p>
            <p>
              Study should have been completed within the last eight years in order to meet currency requirements.
              In some courses, this period is shorter (for example, in professional areas where there are often
              changes to practice requirements). In those cases, a full work experience and study history may be
              taken into consideration
            </p>
          </div>
          <div class="mb-3 requiredinput">
            <label class="form-label" for="institutionSeachBox">Filter by Institution</label>
            <InstitutionAutocomplete v-model="selectedInstitution" :institutions="institutions" />
          </div>
        </div>
        <div class="floater">
          <div id="PreviousStudyBox" class="mb-3">
            <label class="form-label" for="searchBox">Previous study</label>
            <input
              id="searchBox"
              v-model="searchInput"
              class="form-control"
              placeholder="Enter subject, or pathway course title or leave blank to browse all results"
              data-admissions-name="externalSearch"
              data-admissions-group="CourseInfo"
              @keydown.enter.prevent="search"
            >
            <div class="example">Eg: CSE1OOF, politics, Diploma of Business.</div>
          </div>
          <button id="searchBtn" class="btn btn-primary" type="button" @click="search">Search</button>
        </div>

        <div v-if="resultsVisible" id="resultsAccordion" class="accordion results-accordion">
          <div class="button_floater">
            <button
              id="showCreditBtn"
              class="btn btn-primary"
              :class="{ invisible: selectedCount === 0 }"
              type="button"
              :disabled="previewLoading"
              @click="showPotentialCredit"
            >Click here to view your potential credit</button>
          </div>
          <ResultSection section="course" title="Pathway Courses" :count="courseTotal" @opened="onSectionOpened">
            <div id="CourseListContain">
              <p>Select a Course Pathway for multiple subject credits or select individual subjects for credit assessment.</p>
              <h3>Pathway Courses</h3>
              <p>Below are the pathway agreements we have with other institutions. Please select what course you have completed to view the credit you may receive.</p>
              <CourseResultTable
                :courses="courses"
                :selected-keys="selectedCourseKeys"
                :selected-institution-name="selectedInstitution?.name ?? null"
                @select="selectCourse"
                @remove="removeCourse"
              />
              <PaginationControls
                id-prefix="course"
                :offset="courseOffset"
                :limit="courseLimit"
                :total="courseTotal"
                :loading="courseLoading"
                @previous="moveCoursePage(-1)"
                @next="moveCoursePage(1)"
              />
            </div>
          </ResultSection>
          <ResultSection section="subject" title="Subjects" :count="subjectTotal" @opened="onSectionOpened">
            <div id="RuleListContain">
              <h3>Subjects</h3>
              <p>Below are the subjects offered by other institutions. Please select what subjects you have completed. Depending on your institution subjects may be called units.</p>
              <SubjectResultTable
                :subjects="subjects"
                :selected-keys="selectedSubjectKeys"
                @select="selectSubject"
                @remove="removeSubject"
              />
              <PaginationControls
                id-prefix="subject"
                :offset="subjectOffset"
                :limit="subjectLimit"
                :total="subjectTotal"
                :loading="subjectLoading"
                @previous="moveSubjectPage(-1)"
                @next="moveSubjectPage(1)"
              />
            </div>
          </ResultSection>
        </div>
      </div>
    </div>
  </main>
  <CreditPreviewModal
    v-if="previewOpen"
    :preview="preview"
    :loading="previewLoading"
    :refreshing="previewRefreshing"
    :error-message="previewError"
    :apply-now-visible="configuration?.applyNowButtonVisibility !== 'hide'"
    :selected-course-key="selectedCourseKey"
    @close="closePreview"
    @disclaimer="showDisclaimer"
    @remove-subject="removePreviewSubject"
    @remove-course="removePreviewCourse"
  />
</template>

<style lang="scss" scoped>
.credit-search-page {
  margin-bottom: 10em;
  background: #fff;

  h1,
  h3 {
    color: #e42312;
  }

  h1 {
    font-size: 36px;
    line-height: 40px;
  }

  .calculatorPreText,
  .floater {
    margin-top: 1em;
  }

  .button_floater {
    margin: 0 auto;
    text-align: center;
    position: sticky;
    top: 1em;
    z-index: 8;
  }

  .invisible {
    visibility: hidden;
  }

  .example {
    margin-top: 0.25rem;
  }

  .requiredinput label {
    font-weight: 700;
  }
}
</style>
